import React, { useEffect, useRef, useState } from 'react';
import { X, Send, Sparkles } from 'lucide-react';
import axios from 'axios';

const baseURL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STARTER_MESSAGES = [
  'What is ATTP NEXUS?',
  'Tell me about the Hospital module',
  'How much does it cost?',
  'How do I sign up?',
];

export default function AIChatWidget({ context }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm the ATTP NEXUS Bot. Ask me anything — modules, pricing, how to get started, or what we can do for your organization." },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('attp_ai_sid') || null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: trimmed }]);
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${baseURL}/ai/chat`,
        { message: trimmed, session_id: sessionId, context },
        { timeout: 30000 }
      );
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('attp_ai_sid', data.session_id);
      }
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Something went wrong. Please try again.';
      setMessages((m) => [...m, { role: 'assistant', content: `⚠️ ${detail}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 left-5 z-40 group inline-flex items-center gap-2.5 pl-4 pr-5 py-3 bg-amber text-navy rounded-full shadow-2xl hover:shadow-amber/50 hover:scale-105 transition-all font-semibold text-sm"
          data-testid="ai-chat-open"
        >
          <span className="relative">
            <Sparkles className="w-4 h-4" strokeWidth={2.5} />
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          </span>
          ATTP NEXUS Bot
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-5 left-5 z-50 w-[92vw] sm:w-[380px] h-[560px] max-h-[80vh] bg-white rounded-2xl shadow-2xl border border-neutral-200 flex flex-col overflow-hidden"
          data-testid="ai-chat-panel"
        >
          <div className="px-4 py-3 bg-navy text-white flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-full bg-amber flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-navy" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display font-semibold text-sm leading-tight">ATTP NEXUS Bot</div>
                <div className="text-[10px] text-white/60 uppercase tracking-wider flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Online · Ready to help
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded-md transition-colors" data-testid="ai-chat-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-neutral-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user' ? 'bg-navy text-white rounded-br-md' : 'bg-white text-navy border border-neutral-200 rounded-bl-md'
                }`}>{m.content}</div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-neutral-200 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber animate-pulse" />
                  <span className="h-1.5 w-1.5 rounded-full bg-amber animate-pulse" style={{ animationDelay: '0.2s' }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-amber animate-pulse" style={{ animationDelay: '0.4s' }} />
                </div>
              </div>
            )}
          </div>

          {messages.length <= 1 && !busy && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5 bg-neutral-50">
              {STARTER_MESSAGES.map((s) => (
                <button key={s} onClick={() => send(s)} className="text-[11px] px-2.5 py-1 rounded-full bg-white border border-neutral-200 text-navy hover:border-amber hover:bg-amber/5 transition-colors" data-testid={`ai-starter-${s.slice(0, 12)}`}>
                  {s}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-neutral-200 bg-white px-3 py-3 flex items-center gap-2 flex-shrink-0">
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask anything…" disabled={busy} className="flex-1 px-3 py-2 text-sm bg-neutral-50 border border-neutral-200 rounded-full outline-none focus:border-navy focus:bg-white transition-colors disabled:opacity-60" data-testid="ai-chat-input" />
            <button type="submit" disabled={busy || !input.trim()} className="h-9 w-9 rounded-full bg-navy text-white flex items-center justify-center disabled:opacity-40 hover:bg-navy/90 transition-colors flex-shrink-0" data-testid="ai-chat-send">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
